import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Calendar, Smartphone, Mail, Ticket, Zap, CheckCircle2, Home, Download, Sparkles } from 'lucide-react';
import QRCode from 'react-qr-code';

const TicketScreen = () => {
    const { state } = useLocation();
    const navigate = useNavigate();
    const { bookingData } = state || {};

    if (!bookingData) return (
        <div className="min-h-screen bg-cream flex flex-col items-center justify-center p-10 text-center">
            <h2 className="text-3xl font-playfair font-black text-secondary mb-4">No Ticket Found</h2>
            <button onClick={() => navigate('/')} className="px-8 py-4 bg-primary text-white rounded-2xl font-bold shadow-lg">Back to Home</button>
        </div>
    );

    const { eventTitle, eventVenue, eventDateTime, selectedSeats, totalAmount, phone, email, txnId } = bookingData;

    return (
        <div className="min-h-screen bg-cream py-12 px-4 font-outfit text-secondary flex flex-col items-center relative overflow-hidden">
            {/* Background Decor */}
            <div className="absolute top-0 left-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl translate-x-1/3 translate-y-1/3"></div>

            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md bg-white rounded-[3rem] overflow-hidden shadow-2xl border border-primary/10 relative z-10"
            >
                <div className="bg-secondary p-10 text-white text-center relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-primary/20 rounded-full blur-2xl -translate-x-1/2 -translate-y-1/2"></div>
                    <div className="w-16 h-16 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center mx-auto mb-4 border border-white/10">
                        <CheckCircle2 className="text-primary" size={32} />
                    </div>
                    <h2 className="text-[10px] font-black uppercase tracking-[0.3em] text-primary mb-2">Divine Journey Confirmed</h2>
                    <h3 className="text-3xl font-playfair font-black tracking-tight">{eventTitle}</h3>
                </div>

                <div className="p-10 space-y-8">
                    <div className="space-y-6">
                        <div className="flex gap-5">
                            <div className="w-10 h-10 bg-cream rounded-xl flex items-center justify-center text-primary shrink-0">
                                <MapPin size={20}/>
                            </div>
                            <div>
                                <p className="text-[10px] font-black text-secondary/40 uppercase tracking-widest mb-1">Sanctuary / Venue</p>
                                <p className="text-base font-bold text-secondary">{eventVenue}</p>
                            </div>
                        </div>
                        <div className="flex gap-5">
                            <div className="w-10 h-10 bg-cream rounded-xl flex items-center justify-center text-primary shrink-0">
                                <Calendar size={20}/>
                            </div>
                            <div>
                                <p className="text-[10px] font-black text-secondary/40 uppercase tracking-widest mb-1">Divine Timing</p>
                                <p className="text-base font-bold text-secondary">{eventDateTime}</p>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-between items-center py-8 border-y border-dashed border-primary/20">
                        <div>
                            <p className="text-[10px] font-black text-secondary/40 uppercase tracking-widest mb-3">Selected Seats</p>
                            <div className="flex flex-wrap gap-2">
                                {selectedSeats.map(s => (
                                    <span key={s} className="bg-primary text-white text-[10px] font-black px-4 py-1.5 rounded-xl shadow-sm">
                                        {s}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="text-[10px] font-black text-secondary/40 uppercase tracking-widest mb-1">Contribution</p>
                            <p className="text-3xl font-black text-secondary italic">₹{totalAmount}</p>
                        </div>
                    </div>

                    <div className="flex flex-col items-center py-8 bg-cream/50 rounded-[2.5rem] border border-primary/5">
                        <div className="bg-white p-4 rounded-3xl shadow-lg border border-primary/10">
                            <QRCode value={txnId || 'N/A'} size={150} fgColor="#3E2723" />
                        </div>
                        <p className="mt-6 text-[10px] font-black text-secondary/30 uppercase tracking-[0.4em]">Scan for Entry</p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="p-5 bg-cream/30 rounded-2xl border border-primary/5">
                            <p className="text-[9px] font-black text-secondary/40 uppercase tracking-widest mb-1">Phone</p>
                            <p className="text-xs font-bold text-secondary truncate">{phone}</p>
                        </div>
                        <div className="p-5 bg-cream/30 rounded-2xl border border-primary/5">
                            <p className="text-[9px] font-black text-secondary/40 uppercase tracking-widest mb-1">Email</p>
                            <p className="text-xs font-bold text-secondary truncate">{email}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-secondary py-6 text-center">
                    <p className="text-white/40 text-[10px] font-black tracking-[0.5em] uppercase italic">Rotaract Bhavnagar Youth</p>
                </div>
            </motion.div>

            <div className="flex flex-col sm:flex-row gap-4 mt-10 w-full max-w-md px-4">
                <button
                    onClick={() => window.print()}
                    className="flex-1 bg-white border-2 border-primary/10 py-5 rounded-[1.5rem] font-black text-sm text-secondary flex items-center justify-center gap-3 shadow-lg hover:bg-cream transition-all"
                >
                    <Download size={20} className="text-primary"/> Save Ticket
                </button>
                <button
                    onClick={() => navigate('/')}
                    className="flex-1 bg-primary text-white py-5 rounded-[1.5rem] font-black text-sm flex items-center justify-center gap-3 shadow-xl shadow-primary/20 hover:bg-secondary transition-all"
                >
                    <Home size={20}/> Return Home
                </button>
            </div>

            <p className="mt-12 text-[10px] font-black text-secondary/20 uppercase tracking-[0.5em] italic">A Partnership Built on Trust</p>
        </div>
    );
};

export default TicketScreen;
